/**
 * ISO base media file format helpers.
 *
 * MP4 and MOV are the same container family. We mux a standard MP4 and, when a
 * `.mov` is requested, rewrite the `ftyp` brand to QuickTime's `qt  ` so the
 * file is a first-class QuickTime movie (this is exactly what `ffmpeg -f mov`
 * emits). The box keeps its original size, so the rewrite is a safe in-place
 * patch of the file header.
 */

export interface BoxInfo {
  type: string;
  start: number;
  size: number;
}

export function readTopLevelBoxes(buffer: ArrayBuffer): BoxInfo[] {
  const view = new DataView(buffer);
  const boxes: BoxInfo[] = [];
  let offset = 0;
  while (offset + 8 <= buffer.byteLength) {
    const size = view.getUint32(offset);
    const type = String.fromCharCode(
      view.getUint8(offset + 4),
      view.getUint8(offset + 5),
      view.getUint8(offset + 6),
      view.getUint8(offset + 7),
    );
    if (size === 0) {
      boxes.push({ type, start: offset, size: buffer.byteLength - offset });
      break;
    }
    if (size < 8 || offset + size > buffer.byteLength) break;
    boxes.push({ type, start: offset, size });
    offset += size;
  }
  return boxes;
}

export function isIsoBmff(buffer: ArrayBuffer): boolean {
  const boxes = readTopLevelBoxes(buffer);
  if (boxes.length === 0) return false;
  const first = boxes[0].type;
  return first === 'ftyp' || first === 'styp' || first === 'moov';
}

const asciiBytes = (text: string): number[] => Array.from(text).map((char) => char.charCodeAt(0));

/**
 * Rewrite an MP4 `ftyp` header into a QuickTime `ftyp` header, in place.
 * Returns the same buffer for convenience.
 */
export function patchQuickTimeBrand(buffer: ArrayBuffer): ArrayBuffer {
  const boxes = readTopLevelBoxes(buffer);
  const ftyp = boxes.find((box) => box.type === 'ftyp');
  if (!ftyp || ftyp.size < 16) return buffer;

  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  bytes.set(asciiBytes('qt  '), ftyp.start + 8); // major_brand
  view.setUint32(ftyp.start + 12, 0x0000_0200); // minor_version (QuickTime 2.0)

  // Every remaining 4-byte slot in the box is a compatible brand. Replacing
  // them in place keeps the box length identical.
  const brandAreaStart = ftyp.start + 16;
  const brandAreaEnd = ftyp.start + ftyp.size;
  for (let offset = brandAreaStart; offset + 4 <= brandAreaEnd; offset += 4) {
    bytes.set(asciiBytes('qt  '), offset);
  }
  return buffer;
}

export function readMajorBrand(buffer: ArrayBuffer): string | null {
  const ftyp = readTopLevelBoxes(buffer).find((box) => box.type === 'ftyp');
  if (!ftyp) return null;
  const bytes = new Uint8Array(buffer, ftyp.start + 8, 4);
  return String.fromCharCode(...bytes);
}
